import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  LogOut,
  Users,
  GraduationCap,
  DollarSign,
  Plus,
  Trash2,
  Check,
  Upload,
  History,
  RefreshCw,
  FileCheck,
  FileX,
  Calendar,
  Download,
  Eye,
  BarChart3,
  HardDrive,
  Edit,
  FileText,
  Video,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { ThemeToggle } from "../components/ThemeToggle";
import { useTheme } from "../context/ThemeContext";
import { AdminFileManager } from "../components/AdminFileManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Theme-aware logos (SVG with transparent background)
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/drr8t3sh_Dark_2.svg";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/6ix3baet_Light_2.svg";

const COURSE_CATEGORIES = {
  "Instruments": [
    "Drums",
    "Bass",
    "Piano",
    "Guitar",
    "Trumpet",
    "Trombone",
    "Clarinet",
    "Flute",
    "Oboe",
    "Alto Sax",
    "Tenor Sax",
    "Soprano Sax",
    "Baritone Sax",
    "Vocal",
  ],
  "Theory & Composition": [
    "Music Theory",
    "Jazz Theory",
    "Arrangement",
    "Improvisation",
  ],
  "Production": [
    "Studio Production",
    "AI Creative Music",
  ],
};

// Flat list for dropdowns
const ALL_COURSES = Object.values(COURSE_CATEGORIES).flat();

const HOURLY_RATE = 27.50;
const HALF_HOUR_RATE = 22.00;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const logoUrl = theme === 'dark' ? LOGO_DARK : LOGO_LIGHT;
  const [activeTab, setActiveTab] = useState("instructors");
  const [instructors, setInstructors] = useState([]);
  const [students, setStudents] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Student form state
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [studentForm, setStudentForm] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "",
    date_of_birth: "",
    enrolled_courses: [],
  });

  // Material upload state
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [materialForm, setMaterialForm] = useState({
    title: "",
    description: "",
    course: "",
    file: null,
    material_type: "pdf",
  });
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialTypeFilter, setMaterialTypeFilter] = useState("pdf");
  const fileInputRef = useRef(null);

  // Student progress state
  const [studentProgress, setStudentProgress] = useState([]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("mms_token")}` },
  });

  const fetchData = async () => {
    try {
      const [instructorsRes, studentsRes, materialsRes, paymentsRes, progressRes] = await Promise.all([
        axios.get(`${API}/admin/instructors`, getAuthHeaders()),
        axios.get(`${API}/admin/students`, getAuthHeaders()),
        axios.get(`${API}/materials`, getAuthHeaders()),
        axios.get(`${API}/admin/payments`, getAuthHeaders()),
        axios.get(`${API}/admin/students/progress/all`, getAuthHeaders()).catch(() => ({ data: [] })),
      ]);
      setInstructors(instructorsRes.data);
      setStudents(studentsRes.data);
      setMaterials(materialsRes.data);
      setPayments(paymentsRes.data);
      setStudentProgress(progressRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.clear();
        navigate("/admin-login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const handleProcessPayment = async (instructorId) => {
    try {
      const result = await axios.post(
        `${API}/admin/process-payment/${instructorId}`,
        {},
        getAuthHeaders()
      );
      toast.success(`Payment of $${result.data.amount.toFixed(2)} processed!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process payment");
    }
  };

  const handleDeleteInstructor = async (instructorId) => {
    if (!window.confirm("Are you sure you want to delete this instructor?")) return;
    try {
      await axios.delete(`${API}/admin/instructors/${instructorId}`, getAuthHeaders());
      toast.success("Instructor deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete instructor");
    }
  };

  const handleToggleInstructorActive = async (instructor) => {
    try {
      await axios.put(
        `${API}/admin/instructors/${instructor.id}`,
        { is_active: !instructor.is_active },
        getAuthHeaders()
      );
      toast.success(`Instructor ${instructor.is_active ? "deactivated" : "activated"}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update instructor status");
    }
  };

  const handleCreateStudent = async () => {
    if (!studentForm.name || studentForm.enrolled_courses.length === 0) {
      toast.error("Name and at least one course are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/students`, studentForm, getAuthHeaders());
      toast.success("Student created successfully");
      setShowStudentForm(false);
      setStudentForm({ name: "", email: "", phone: "", gender: "", date_of_birth: "", enrolled_courses: [] });
      fetchData();
    } catch (error) {
      toast.error("Failed to create student");
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm("Are you sure you want to delete this student?")) return;
    try {
      await axios.delete(`${API}/admin/students/${studentId}`, getAuthHeaders());
      toast.success("Student deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete student");
    }
  };

  const handleUploadMaterial = async () => {
    if (!materialForm.title || !materialForm.course || !materialForm.file) {
      toast.error("Title, course, and file are required");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("title", materialForm.title);
      formData.append("description", materialForm.description);
      formData.append("course", materialForm.course);
      formData.append("file", materialForm.file);
      formData.append("material_type", materialForm.material_type);

      await axios.post(`${API}/admin/materials`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("mms_token")}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Material uploaded successfully");
      setShowMaterialForm(false);
      setMaterialForm({ title: "", description: "", course: "", file: null, material_type: "pdf" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload material");
    }
  };

  const handleEditMaterial = async () => {
    if (!editingMaterial) return;
    try {
      const formData = new FormData();
      formData.append("title", editingMaterial.title);
      formData.append("description", editingMaterial.description);
      formData.append("course", editingMaterial.course);

      await axios.put(`${API}/admin/materials/${editingMaterial.id}`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("mms_token")}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Material updated successfully");
      setEditingMaterial(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update material");
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm("Are you sure you want to delete this material?")) return;
    try {
      await axios.delete(`${API}/admin/materials/${materialId}`, getAuthHeaders());
      toast.success("Material deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete material");
    }
  };

  const filteredMaterials = materials.filter(m => m.material_type === materialTypeFilter || !m.material_type && materialTypeFilter === "pdf");

  const toggleStudentCourse = (course) => {
    setStudentForm((prev) => ({
      ...prev,
      enrolled_courses: prev.enrolled_courses.includes(course)
        ? prev.enrolled_courses.filter((c) => c !== course)
        : [...prev.enrolled_courses, course],
    }));
  };

  const calculateAge = (dob) => {
    if (!dob) return "-";
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const totalDue = instructors.reduce((sum, i) => sum + (i.amount_due || 0), 0);
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const activeInstructors = instructors.filter(i => i.is_active).length;
  const contractsUploaded = instructors.filter(i => i.contract_uploaded).length;

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-zinc-950' : 'bg-slate-50'}`}>
        <div className={`w-8 h-8 border-2 rounded-full animate-spin ${theme === 'dark' ? 'border-white/20 border-t-white' : 'border-indigo-200 border-t-indigo-600'}`} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-zinc-950' : 'bg-slate-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 backdrop-blur-xl border-b ${theme === 'dark' ? 'bg-zinc-950/80 border-white/10' : 'bg-white/80 border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={logoUrl} alt="MMS Logo" className="h-12 w-auto object-contain logo-glass" />
            <span className={`text-lg font-semibold hidden sm:block ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>Admin Dashboard</span>
          </div>
          <nav className="flex items-center gap-4">
            <Link to="/materials" className="glass-button-ghost text-sm">
              Materials
            </Link>
            <ThemeToggle />
            <button onClick={handleLogout} className="glass-button-ghost text-sm flex items-center gap-1">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6"
          >
            <Users className={`w-8 h-8 mb-3 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
            <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Instructors</p>
            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`} data-testid="total-instructors">
              {instructors.length}
            </p>
            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
              {activeInstructors} active
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-6"
          >
            <FileCheck className={`w-8 h-8 mb-3 ${contractsUploaded === instructors.length ? 'text-green-400' : 'text-yellow-400'}`} />
            <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Contracts</p>
            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
              {contractsUploaded}/{instructors.length}
            </p>
            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
              uploaded
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-6"
          >
            <GraduationCap className={`w-8 h-8 mb-3 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
            <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Students</p>
            <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`} data-testid="total-students">
              {students.length}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-6"
          >
            <DollarSign className="w-8 h-8 text-yellow-400 mb-3" />
            <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Total Due</p>
            <p className={`text-2xl font-bold mono ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`} data-testid="total-due">
              ${totalDue.toFixed(2)}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card p-6"
          >
            <History className="w-8 h-8 text-green-400 mb-3" />
            <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Total Paid</p>
            <p className={`text-2xl font-bold mono ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`} data-testid="total-paid">
              ${totalPaid.toFixed(2)}
            </p>
          </motion.div>
        </div>

        {/* Payment Rates Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className={`glass-card p-4 mb-6 flex flex-wrap gap-6 items-center ${theme === 'dark' ? 'bg-zinc-900/50' : 'bg-slate-100'}`}
        >
          <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>Payment Rates:</span>
          <span className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
            Hourly: <span className="font-semibold mono">${HOURLY_RATE.toFixed(2)}/hr</span>
          </span>
          <span className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
            30-min Lesson: <span className="font-semibold mono">${HALF_HOUR_RATE.toFixed(2)}</span>
          </span>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className={`glass-card p-1 w-full sm:w-auto flex-wrap ${theme === 'light' ? 'bg-slate-100' : ''}`}>
            <TabsTrigger
              value="instructors"
              data-testid="tab-instructors"
              className={`${theme === 'dark' ? 'data-[state=active]:bg-white data-[state=active]:text-black' : 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white'}`}
            >
              Instructors
            </TabsTrigger>
            <TabsTrigger
              value="students"
              data-testid="tab-students"
              className={`${theme === 'dark' ? 'data-[state=active]:bg-white data-[state=active]:text-black' : 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white'}`}
            >
              Students
            </TabsTrigger>
            <TabsTrigger
              value="progress"
              data-testid="tab-progress"
              className={`${theme === 'dark' ? 'data-[state=active]:bg-white data-[state=active]:text-black' : 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white'}`}
            >
              Progress
            </TabsTrigger>
            <TabsTrigger
              value="materials"
              data-testid="tab-materials"
              className={`${theme === 'dark' ? 'data-[state=active]:bg-white data-[state=active]:text-black' : 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white'}`}
            >
              Materials
            </TabsTrigger>
            <TabsTrigger
              value="payments"
              data-testid="tab-payments"
              className={`${theme === 'dark' ? 'data-[state=active]:bg-white data-[state=active]:text-black' : 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white'}`}
            >
              Payment History
            </TabsTrigger>
            <TabsTrigger
              value="files"
              data-testid="tab-files"
              className={`${theme === 'dark' ? 'data-[state=active]:bg-white data-[state=active]:text-black' : 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white'}`}
            >
              <HardDrive className="w-4 h-4 mr-1" />
              Files
            </TabsTrigger>
          </TabsList>

          {/* Instructors Tab */}
          <TabsContent value="instructors">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card overflow-hidden"
            >
              <div className={`p-6 border-b flex justify-between items-center ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
                <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>Manage Instructors</h2>
                <Button onClick={fetchData} className="glass-button-secondary py-2 px-4">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="glass-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Age</th>
                      <th>Contract</th>
                      <th>Courses</th>
                      <th>Amount Due</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {instructors.map((instructor) => (
                      <tr key={instructor.id} data-testid={`instructor-row-${instructor.id}`}>
                        <td className="font-medium">
                          {instructor.first_name && instructor.last_name 
                            ? `${instructor.first_name} ${instructor.last_name}`
                            : instructor.full_legal_name}
                        </td>
                        <td className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>{instructor.email}</td>
                        <td className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>
                          {calculateAge(instructor.date_of_birth)}
                        </td>
                        <td>
                          {instructor.contract_uploaded ? (
                            <button
                              data-testid={`download-contract-${instructor.id}`}
                              onClick={() => {
                                const token = localStorage.getItem("mms_token");
                                fetch(`${API}/admin/instructors/${instructor.id}/contract`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                })
                                .then(res => res.blob())
                                .then(blob => {
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `contract_${instructor.first_name || 'instructor'}.pdf`;
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                })
                                .catch(() => toast.error("Failed to download contract"));
                              }}
                              className="flex items-center gap-1 text-green-400 hover:text-green-300 transition-colors"
                            >
                              <FileCheck className="w-4 h-4" />
                              <Download className="w-3 h-3" />
                            </button>
                          ) : (
                            <span className="flex items-center gap-1 text-red-400">
                              <FileX className="w-4 h-4" />
                              <span className="text-xs">Missing</span>
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {instructor.authorized_courses?.slice(0, 2).map((c) => (
                              <span
                                key={c}
                                className={`text-xs px-2 py-0.5 rounded-full ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-100'}`}
                              >
                                {c}
                              </span>
                            ))}
                            {instructor.authorized_courses?.length > 2 && (
                              <span className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                                +{instructor.authorized_courses.length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="mono font-semibold">
                          ${(instructor.amount_due || 0).toFixed(2)}
                        </td>
                        <td>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              instructor.is_active
                                ? "bg-green-500/20 text-green-400"
                                : "bg-red-500/20 text-red-400"
                            }`}
                          >
                            {instructor.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            {instructor.amount_due > 0 && (
                              <Button
                                data-testid={`pay-instructor-${instructor.id}`}
                                onClick={() => handleProcessPayment(instructor.id)}
                                className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg"
                              >
                                <Check className="w-3 h-3 mr-1" />
                                Pay
                              </Button>
                            )}
                            <Button
                              data-testid={`toggle-instructor-${instructor.id}`}
                              onClick={() => handleToggleInstructorActive(instructor)}
                              className={`text-xs px-3 py-1 rounded-lg ${theme === 'dark' ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
                            >
                              {instructor.is_active ? "Deactivate" : "Activate"}
                            </Button>
                            <Button
                              data-testid={`delete-instructor-${instructor.id}`}
                              onClick={() => handleDeleteInstructor(instructor.id)}
                              className="text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 px-2 py-1 rounded-lg"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {instructors.length === 0 && (
                  <div className={`p-12 text-center ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                    No instructors registered yet
                  </div>
                )}
              </div>
            </motion.div>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card overflow-hidden"
            >
              <div className={`p-6 border-b flex justify-between items-center ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
                <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>Manage Students</h2>
                <Dialog open={showStudentForm} onOpenChange={setShowStudentForm}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-student-btn" className="glass-button-primary py-2 px-4">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Student
                    </Button>
                  </DialogTrigger>
                  <DialogContent className={`glass-card max-w-md ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
                    <DialogHeader>
                      <DialogTitle className={theme === 'dark' ? 'text-white' : 'text-slate-800'}>Add New Student</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="form-label">Name *</label>
                        <Input
                          data-testid="student-name-input"
                          value={studentForm.name}
                          onChange={(e) =>
                            setStudentForm((prev) => ({ ...prev, name: e.target.value }))
                          }
                          className="glass-input"
                          placeholder="Student name"
                        />
                      </div>
                      <div>
                        <label className="form-label">Email</label>
                        <Input
                          data-testid="student-email-input"
                          type="email"
                          value={studentForm.email}
                          onChange={(e) =>
                            setStudentForm((prev) => ({ ...prev, email: e.target.value }))
                          }
                          className="glass-input"
                          placeholder="student@example.com"
                        />
                      </div>
                      <div>
                        <label className="form-label">Phone</label>
                        <Input
                          data-testid="student-phone-input"
                          value={studentForm.phone}
                          onChange={(e) =>
                            setStudentForm((prev) => ({ ...prev, phone: e.target.value }))
                          }
                          className="glass-input"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="form-label">Gender</label>
                          <Select
                            value={studentForm.gender}
                            onValueChange={(value) =>
                              setStudentForm((prev) => ({ ...prev, gender: value }))
                            }
                          >
                            <SelectTrigger data-testid="student-gender-select" className="glass-input">
                              <SelectValue placeholder="Select gender" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Male">Male</SelectItem>
                              <SelectItem value="Female">Female</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="form-label">Date of Birth</label>
                          <Input
                            data-testid="student-dob-input"
                            type="date"
                            value={studentForm.date_of_birth}
                            onChange={(e) =>
                              setStudentForm((prev) => ({ ...prev, date_of_birth: e.target.value }))
                            }
                            className="glass-input"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="form-label">Enrolled Courses *</label>
                        <div className="space-y-4 mt-2 max-h-64 overflow-y-auto pr-2">
                          {Object.entries(COURSE_CATEGORIES).map(([category, courses]) => (
                            <div key={category}>
                              <h5 className={`text-xs font-semibold mb-2 flex items-center gap-1.5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${
                                  category === 'Instruments' ? 'bg-blue-500' :
                                  category === 'Theory & Composition' ? 'bg-purple-500' : 'bg-emerald-500'
                                }`} />
                                {category}
                              </h5>
                              <div className="grid grid-cols-2 gap-1.5">
                                {courses.map((course) => (
                                  <button
                                    key={course}
                                    data-testid={`student-course-${course.toLowerCase().replace(/\s+/g, "-")}`}
                                    type="button"
                                    onClick={() => toggleStudentCourse(course)}
                                    className={`p-2 rounded-lg text-sm transition-all ${
                                      studentForm.enrolled_courses.includes(course)
                                        ? theme === 'dark'
                                          ? "bg-white text-black"
                                          : "bg-indigo-600 text-white"
                                        : theme === 'dark'
                                          ? "bg-white/10 text-white hover:bg-white/20"
                                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                                    }`}
                                  >
                                    {course}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Button
                        data-testid="submit-student-btn"
                        onClick={handleCreateStudent}
                        className="glass-button-primary w-full mt-4"
                      >
                        Add Student
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="overflow-x-auto">
                <table className="glass-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Gender</th>
                      <th>Birth Date</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Courses</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <tr key={student.id} data-testid={`student-row-${student.id}`}>
                        <td className="font-medium">{student.name}</td>
                        <td className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>
                          {student.gender || "-"}
                        </td>
                        <td className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>
                          {student.date_of_birth ? new Date(student.date_of_birth).toLocaleDateString() : "-"}
                        </td>
                        <td className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>{student.email || "-"}</td>
                        <td className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>{student.phone || "-"}</td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {student.enrolled_courses?.slice(0, 2).map((c) => (
                              <span
                                key={c}
                                className={`text-xs px-2 py-0.5 rounded-full ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-100'}`}
                              >
                                {c}
                              </span>
                            ))}
                            {student.enrolled_courses?.length > 2 && (
                              <span className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                                +{student.enrolled_courses.length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              student.is_active
                                ? "bg-green-500/20 text-green-400"
                                : "bg-red-500/20 text-red-400"
                            }`}
                          >
                            {student.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <Button
                            data-testid={`delete-student-${student.id}`}
                            onClick={() => handleDeleteStudent(student.id)}
                            className="text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 px-2 py-1 rounded-lg"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {students.length === 0 && (
                  <div className={`p-12 text-center ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                    No students added yet. Add your first student above.
                  </div>
                )}
              </div>
            </motion.div>
          </TabsContent>

          {/* Progress Tab */}
          <TabsContent value="progress">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card overflow-hidden"
            >
              <div className={`p-6 border-b flex justify-between items-center ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
                <h2 className={`text-xl font-semibold flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                  <BarChart3 className="w-5 h-5" />
                  Student Progress Tracking
                </h2>
                <Button onClick={fetchData} className="glass-button-secondary py-2 px-4">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="glass-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Total Lessons</th>
                      <th>Total Hours</th>
                      <th>Courses Attended</th>
                      <th>Last Lesson</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentProgress.map((progress) => (
                      <tr key={progress.student_id} data-testid={`progress-row-${progress.student_id}`}>
                        <td className="font-medium">{progress.student_name}</td>
                        <td className={theme === 'dark' ? 'text-white' : 'text-slate-800'}>
                          <span className="mono font-semibold">{progress.total_lessons}</span>
                        </td>
                        <td className={theme === 'dark' ? 'text-white' : 'text-slate-800'}>
                          <span className="mono">{progress.total_hours}h</span>
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(progress.lessons_by_course || {}).map(([course, count]) => (
                              <span
                                key={course}
                                className={`text-xs px-2 py-0.5 rounded-full ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-100'}`}
                              >
                                {course}: {count}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                          {progress.last_lesson_date || "-"}
                        </td>
                        <td>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              progress.is_active
                                ? "bg-green-500/20 text-green-400"
                                : "bg-red-500/20 text-red-400"
                            }`}
                          >
                            {progress.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {studentProgress.length === 0 && (
                  <div className={`p-12 text-center ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                    No student progress data yet. Students need to take lessons to track progress.
                  </div>
                )}
              </div>
            </motion.div>
          </TabsContent>

          {/* Materials Tab */}
          <TabsContent value="materials">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card overflow-hidden"
            >
              <div className={`p-6 border-b flex justify-between items-center ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
                <div className="flex items-center gap-4">
                  <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>Course Materials</h2>
                  {/* Material Type Toggle */}
                  <div className={`flex rounded-lg overflow-hidden border ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
                    <button
                      data-testid="filter-pdf-materials"
                      onClick={() => setMaterialTypeFilter("pdf")}
                      className={`px-4 py-2 text-sm flex items-center gap-2 transition-all ${
                        materialTypeFilter === "pdf"
                          ? theme === 'dark' ? 'bg-white text-black' : 'bg-indigo-600 text-white'
                          : theme === 'dark' ? 'bg-white/5 text-slate-400 hover:bg-white/10' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <FileText className="w-4 h-4" />
                      PDFs
                    </button>
                    <button
                      data-testid="filter-video-materials"
                      onClick={() => setMaterialTypeFilter("video")}
                      className={`px-4 py-2 text-sm flex items-center gap-2 transition-all ${
                        materialTypeFilter === "video"
                          ? theme === 'dark' ? 'bg-white text-black' : 'bg-indigo-600 text-white'
                          : theme === 'dark' ? 'bg-white/5 text-slate-400 hover:bg-white/10' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <Video className="w-4 h-4" />
                      Videos
                    </button>
                  </div>
                </div>
                <Dialog open={showMaterialForm} onOpenChange={setShowMaterialForm}>
                  <DialogTrigger asChild>
                    <Button data-testid="upload-material-btn" className="glass-button-primary py-2 px-4">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Material
                    </Button>
                  </DialogTrigger>
                  <DialogContent className={`glass-card max-w-md ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
                    <DialogHeader>
                      <DialogTitle className={theme === 'dark' ? 'text-white' : 'text-slate-800'}>Upload Course Material</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      {/* Material Type Selection */}
                      <div>
                        <label className="form-label">Material Type *</label>
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            data-testid="material-type-pdf"
                            onClick={() => setMaterialForm(prev => ({ ...prev, material_type: "pdf" }))}
                            className={`flex-1 p-3 rounded-lg border flex items-center justify-center gap-2 transition-all ${
                              materialForm.material_type === "pdf"
                                ? theme === 'dark' ? 'bg-white text-black border-white' : 'bg-indigo-600 text-white border-indigo-600'
                                : theme === 'dark' ? 'bg-white/5 text-slate-400 border-white/10 hover:border-white/20' : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <FileText className="w-4 h-4" />
                            PDF
                          </button>
                          <button
                            type="button"
                            data-testid="material-type-video"
                            onClick={() => setMaterialForm(prev => ({ ...prev, material_type: "video" }))}
                            className={`flex-1 p-3 rounded-lg border flex items-center justify-center gap-2 transition-all ${
                              materialForm.material_type === "video"
                                ? theme === 'dark' ? 'bg-white text-black border-white' : 'bg-indigo-600 text-white border-indigo-600'
                                : theme === 'dark' ? 'bg-white/5 text-slate-400 border-white/10 hover:border-white/20' : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <Video className="w-4 h-4" />
                            Video
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="form-label">Title *</label>
                        <Input
                          data-testid="material-title-input"
                          value={materialForm.title}
                          onChange={(e) =>
                            setMaterialForm((prev) => ({ ...prev, title: e.target.value }))
                          }
                          className="glass-input"
                          placeholder="Material title"
                        />
                      </div>
                      <div>
                        <label className="form-label">Description</label>
                        <textarea
                          data-testid="material-description-input"
                          value={materialForm.description}
                          onChange={(e) =>
                            setMaterialForm((prev) => ({ ...prev, description: e.target.value }))
                          }
                          className="glass-input w-full min-h-[80px] resize-none"
                          placeholder="Brief description of the material"
                        />
                      </div>
                      <div>
                        <label className="form-label">Course *</label>
                        <Select
                          value={materialForm.course}
                          onValueChange={(value) =>
                            setMaterialForm((prev) => ({ ...prev, course: value }))
                          }
                        >
                          <SelectTrigger data-testid="material-course-select" className="glass-input">
                            <SelectValue placeholder="Select course" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(COURSE_CATEGORIES).map(([category, courses]) => (
                              <div key={category}>
                                <div className="px-2 py-1.5 text-xs font-semibold text-slate-500">{category}</div>
                                {courses.map((course) => (
                                  <SelectItem key={course} value={course}>
                                    {course}
                                  </SelectItem>
                                ))}
                              </div>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="form-label">
                          {materialForm.material_type === "pdf" ? "PDF File *" : "Video File *"}
                        </label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept={materialForm.material_type === "pdf" ? ".pdf,application/pdf" : "video/*"}
                          data-testid="material-file-input"
                          onChange={(e) =>
                            setMaterialForm((prev) => ({
                              ...prev,
                              file: e.target.files?.[0] || null,
                            }))
                          }
                          className="hidden"
                        />
                        <Button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="glass-input w-full justify-start text-left"
                        >
                          {materialForm.file ? materialForm.file.name : `Choose ${materialForm.material_type === "pdf" ? "PDF" : "video"} file...`}
                        </Button>
                      </div>
                      <Button
                        data-testid="submit-material-btn"
                        onClick={handleUploadMaterial}
                        className="glass-button-primary w-full mt-4"
                      >
                        Upload Material
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Edit Material Dialog */}
              <Dialog open={!!editingMaterial} onOpenChange={(open) => !open && setEditingMaterial(null)}>
                <DialogContent className={`glass-card max-w-md ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
                  <DialogHeader>
                    <DialogTitle className={theme === 'dark' ? 'text-white' : 'text-slate-800'}>Edit Material</DialogTitle>
                  </DialogHeader>
                  {editingMaterial && (
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="form-label">Title *</label>
                        <Input
                          data-testid="edit-material-title"
                          value={editingMaterial.title}
                          onChange={(e) =>
                            setEditingMaterial((prev) => ({ ...prev, title: e.target.value }))
                          }
                          className="glass-input"
                        />
                      </div>
                      <div>
                        <label className="form-label">Description</label>
                        <textarea
                          data-testid="edit-material-description"
                          value={editingMaterial.description || ""}
                          onChange={(e) =>
                            setEditingMaterial((prev) => ({ ...prev, description: e.target.value }))
                          }
                          className="glass-input w-full min-h-[80px] resize-none"
                        />
                      </div>
                      <div>
                        <label className="form-label">Course *</label>
                        <Select
                          value={editingMaterial.course}
                          onValueChange={(value) =>
                            setEditingMaterial((prev) => ({ ...prev, course: value }))
                          }
                        >
                          <SelectTrigger data-testid="edit-material-course" className="glass-input">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(COURSE_CATEGORIES).map(([category, courses]) => (
                              <div key={category}>
                                <div className="px-2 py-1.5 text-xs font-semibold text-slate-500">{category}</div>
                                {courses.map((course) => (
                                  <SelectItem key={course} value={course}>
                                    {course}
                                  </SelectItem>
                                ))}
                              </div>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          data-testid="cancel-edit-material"
                          onClick={() => setEditingMaterial(null)}
                          className="flex-1 glass-button-secondary"
                        >
                          Cancel
                        </Button>
                        <Button
                          data-testid="save-edit-material"
                          onClick={handleEditMaterial}
                          className="flex-1 glass-button-primary"
                        >
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              <div className="overflow-x-auto">
                <table className="glass-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Title</th>
                      <th>Course</th>
                      <th>File</th>
                      <th>Uploaded</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMaterials.map((material) => (
                      <tr key={material.id} data-testid={`material-row-${material.id}`}>
                        <td>
                          {material.material_type === "video" ? (
                            <Video className="w-5 h-5 text-purple-500" />
                          ) : (
                            <FileText className="w-5 h-5 text-red-500" />
                          )}
                        </td>
                        <td className="font-medium">{material.title}</td>
                        <td>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-100'}`}>
                            {material.course}
                          </span>
                        </td>
                        <td className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>{material.file_name}</td>
                        <td className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                          {new Date(material.uploaded_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div className="flex gap-1">
                            <Button
                              data-testid={`edit-material-${material.id}`}
                              onClick={() => setEditingMaterial(material)}
                              className={`text-xs px-2 py-1 rounded-lg ${theme === 'dark' ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              data-testid={`delete-material-${material.id}`}
                              onClick={() => handleDeleteMaterial(material.id)}
                              className="text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 px-2 py-1 rounded-lg"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredMaterials.length === 0 && (
                  <div className={`p-12 text-center ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                    No {materialTypeFilter === "pdf" ? "PDF" : "video"} materials uploaded yet. Upload your first material above.
                  </div>
                )}
              </div>
            </motion.div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card overflow-hidden"
            >
              <div className={`p-6 border-b ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
                <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>Payment History</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="glass-table">
                  <thead>
                    <tr>
                      <th>Instructor</th>
                      <th>Amount</th>
                      <th>Hours</th>
                      <th>Processed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.id} data-testid={`payment-row-${payment.id}`}>
                        <td className="font-medium">{payment.instructor_name}</td>
                        <td className="mono text-green-400">${payment.amount.toFixed(2)}</td>
                        <td className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>{payment.hours_paid.toFixed(1)}h</td>
                        <td className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>
                          {new Date(payment.processed_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {payments.length === 0 && (
                  <div className={`p-12 text-center ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                    No payments processed yet
                  </div>
                )}
              </div>
            </motion.div>
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <AdminFileManager instructors={instructors} />
            </motion.div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};
